import React, {useEffect, useMemo, useState} from "react";
import CSVReader from "react-csv-reader";
import CsvTable from "../../../components/partials/csv/csv-table";
import ButtonSubmit from "../../../components/common/form/button-submit";
import {getImportModels, postImport} from "../../../api/import";
import {getSignedRequests} from "../../../api/config";
import {sendMedia} from "../../../utils/apiUtils";
import {useDispatch, useSelector} from "react-redux";
import lodash from "lodash";
import {Warning} from "../../../components/common/alert/banner";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import {getSelectParams} from "../../../utils/converter";
import InputSelect from "../../../components/common/form/input-select";
import ImportModelPopup from "../../../components/common/popup/import-model-popup";
import MD5 from "crypto-js/md5";
import {subscriptionUsageLimits} from "../../../reducers/subscriptionReducer";
import Papa from "papaparse";
import SlideTopPanel from "../../../components/layout/slide-top-panel";
import InputRadioSimple from "../../../components/common/form/input-radio-simple";

const resolveImportOptions = (importData, t) => {
    if (!importData) {
        return [];
    }

    if (Array.isArray(importData.imports) && importData.imports.length > 0) {
        return importData.imports.map((option) => {
            const resolvedParams = Array.isArray(option.params)
                ? option.params.map((param) => ({
                    ...param,
                    data: Array.isArray(param.data) ? param.data : [],
                    label: param.label
                        ?? (param.labelKey
                            ? t(param.labelKey, {defaultValue: param.defaultLabel || param.labelKey})
                            : t(`app.documents.${param.type}_title`, {defaultValue: param.defaultLabel || param.type})),
                    description: param.description
                        ?? (param.descriptionKey
                            ? t(param.descriptionKey, {defaultValue: param.defaultDescription || param.descriptionKey})
                            : t(`app.documents.${param.type}_description`, {defaultValue: param.defaultDescription || ""})),
                }))
                : [];

            return {
                ...option,
                label: option.label
                    ?? (option.labelKey
                        ? t(option.labelKey, {defaultValue: option.defaultLabel || option.labelKey})
                        : t("app.documents.import_body_label", {defaultValue: "Importa righe documento"})),
                params: resolvedParams,
            };
        });
    }

    const fallbackType = importData.type || "document_body";
    const fallbackFields = Array.isArray(importData.fields) ? importData.fields : [];

    return [
        {
            value: fallbackType,
            label: t("app.documents.import_body_label", {defaultValue: "Importa righe documento"}),
            type: fallbackType,
            params: [
                {
                    type: fallbackType,
                    label: t(`app.documents.${fallbackType}_title`, {defaultValue: "Corpo documento"}),
                    description: t(`app.documents.${fallbackType}_description`, {defaultValue: "Importa le righe del documento con le colonne principali."}),
                    data: fallbackFields,
                },
            ],
        },
    ];
};

export default function DocumentBodyImport({
    title,
    isOpen,
    setIsOpen,
    onImportComplete,
    importGroup,
    importData,
    importEntityId = null,
}) {
    const {loggedCompany} = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const {
        register,
        watch,
        setValue,
        control,
        reset,
        formState: {errors},
    } = useForm();

    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [confirmedHeader, setConfirmedHeader] = useState([]);
    const [originalFile, setOriginalFile] = useState(false);

    const [isOpenModel, setOpenModel] = useState(false);
    const [columnsCode, setColumnsCode] = useState(false);
    const [importModels, setImportModels] = useState([]);
    const [isLoadingModels, setLoadingModels] = useState(false);
    const [isFoundModel, setFoundModel] = useState(false);

    const [isLoading, setLoading] = useState(false);
    const [exception, setException] = useState(false);
    const {t} = useTranslation();

    const importOptions = useMemo(
        () => resolveImportOptions(importData, t),
        [importData, t],
    );

    const selectedImport = useMemo(() => (
        Array.isArray(importOptions) && importOptions.length > 0 ? importOptions[0] : null
    ), [importOptions]);
    const selectedElaborationType = watch("elaboration_type");

    const selectedParamsConfig = useMemo(() => {
        if (!selectedImport || !Array.isArray(selectedImport.params)) {
            return null;
        }
        return selectedImport.params.find((param) => param.type === selectedElaborationType) || null;
    }, [selectedImport, selectedElaborationType]);

    const options = {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
    };

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (!selectedImport || !Array.isArray(selectedImport.params)) {
            setValue("elaboration_type", undefined);
            return;
        }

        if (selectedImport.params.length === 1) {
            const [singleParam] = selectedImport.params;
            if (singleParam?.type) {
                setValue("elaboration_type", singleParam.type);
            }
            return;
        }

        if (selectedElaborationType && selectedImport.params.some((param) => param.type === selectedElaborationType)) {
            return;
        }

        const defaultParam = selectedImport.params[0];
        if (defaultParam?.type) {
            setValue("elaboration_type", defaultParam.type);
        }
    }, [selectedImport, selectedElaborationType, setValue]);

    useEffect(() => {
        setColumns([]);
        setRows([]);
        setColumnsCode(false);
        setOriginalFile(false);
        setConfirmedHeader([]);
        setValue("import_model", null);
        setFoundModel(false);
        setException(false);
    }, [selectedParamsConfig, setValue]);

    const downloadCSV = (data, title) => {
        if (!Array.isArray(data) || data.length === 0) {
            return;
        }
        const csv = Papa.unparse([
            data.map((item) => {
                let normalized = item;
                if (typeof normalized === "string") {
                    normalized = normalized.replace(/_/g, " ");
                    normalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
                }
                return normalized;
            }),
        ], {delimiter: ";"});
        const blob = new Blob(["\ufeff", csv], {type: "text/csv;charset=utf-8;"});

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const uploadCsv = async (file) => {
        if (!selectedImport || !selectedParamsConfig) {
            setException(t("app.documents.import_missing_type", {defaultValue: "Seleziona un tipo di import prima di caricare il file."}));
            return;
        }

        setLoading(true);
        getSignedRequests({params: {ctype: file.type, ext: file.name.split(".").pop()}})
            .then((signed) => {
                sendMedia(signed.action, signed, file)
                    .then((response) => {
                        const url = `${signed.action}/${signed.key}`;
                        const data = {};
                        data["path"] = url;
                        data["file_name"] = file.name;
                        data["params"] = confirmedHeader;
                        data["import_type"] = selectedImport.type;
                        data["config_params"] = {elaboration_type: selectedParamsConfig.type};
                        if (importEntityId) {
                            data["entity_id"] = importEntityId;
                        }
                        storeImport(data);
                    })
                    .catch((error) => {
                        setException(error.message);
                        setLoading(false);
                    });
            })
            .catch((error) => {
                setException(error.message);
                setLoading(false);
            });
    };

    const storeImport = async (data) => {
        setLoading(true);
        if (loggedCompany) {
            postImport({company_id: loggedCompany.id, import_group: importGroup, data})
                .then((response) => {
                    dispatch(subscriptionUsageLimits(loggedCompany.id));
                    setLoading(false);
                    onImportComplete(response);
                    clean();
                })
                .catch((error) => {
                    setException(error.message);
                    setLoading(false);
                });
        }
    };

    const handleForce = (data, fileInfo, original) => {
        if (Array.isArray(data) && data.length > 0) {
            const column = [];
            for (const key in data[0]) {
                if (Object.prototype.hasOwnProperty.call(data[0], key)) {
                    column.push(key);
                }
            }
            setColumns(column);
            setColumnsCode(MD5(column.join("-")).toString());
            setRows(data.length > 10 ? data.slice(0, 10) : data);
            setOriginalFile(original);
        }
    };

    const clean = async () => {
        setRows([]);
        setColumns([]);
        setConfirmedHeader([]);
        setImportModels([]);
        setColumnsCode(false);
        setOriginalFile(false);
        setFoundModel(false);
        setException(false);
        reset();
        await setIsOpen(false);
    };

    useEffect(() => {
        if (importModels.length > 0 && columnsCode !== false) {
            const matchedModel = importModels.find((model) => model.code === columnsCode);
            if (matchedModel) {
                setValue("import_model", matchedModel);
                setFoundModel(true);
                setConfirmedHeader(matchedModel?.params ?? []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setValue, importModels, columnsCode]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        async function fetchModels() {
            if (loggedCompany && isOpen && selectedImport?.type) {
                setLoadingModels(true);
                try {
                    const res = await getImportModels({
                        company_id: loggedCompany.id,
                        params: {import_type: selectedImport.type},
                        signal,
                    });
                    setValue("import_model", null);
                    setFoundModel(false);
                    setImportModels(getSelectParams(res.import_models, "name"));
                } catch (error) {
                    setImportModels([]);
                } finally {
                    setLoadingModels(false);
                }
            } else {
                setImportModels([]);
            }
        }
        fetchModels();

        return () => {
            controller.abort();
        };
    }, [selectedImport, setValue, loggedCompany, isOpen]);

    return (
        <>
            <SlideTopPanel
                title={title}
                bg={"bg-white"}
                isOpen={isOpen}
                setIsOpen={() => {
                    setIsOpen(false);
                }}
                size={"max-w-7xl"}
                footer={Footer}
                setClean={() => {
                    clean();
                }}
                extras={{
                    isLoading,
                    confirmedHeader,
                    onStartLoad: () => {
                        if (isFoundModel) {
                            uploadCsv(originalFile);
                        } else {
                            setOpenModel(true);
                        }
                    },
                }}
            >
                <div className="space-y-6 pt-6 pb-5">
                    <div className="grid grid-cols-2 gap-2 mb-6">
                        {selectedImport && (
                            <>
                                <div className="col-span-2 mt-3">
                                    {
                                        selectedImport.params?.map((data, index) => (
                                            <InputRadioSimple
                                                key={index}
                                                name={"elaboration_type"}
                                                errors={errors.elaboration_type}
                                                input={{...register("elaboration_type", {required: true})}}
                                                label={data.label ?? t(`app.documents.${data.type}_title`, {defaultValue: data.type})}
                                                value={data.type}
                                                description={data.description ?? t(`app.documents.${data.type}_description`, {defaultValue: ""})}
                                            />
                                        ))
                                    }
                                </div>

                                {selectedParamsConfig && Array.isArray(selectedParamsConfig.data) && selectedParamsConfig.data.length > 0 && (
                                    <p className="mt-1 flex items-center text-xs text-indigo-600 underline cursor-pointer col-span-2">
                                        <div
                                            onClick={() => {
                                                downloadCSV(
                                                    selectedParamsConfig.data,
                                                    selectedParamsConfig.type || selectedImport.type,
                                                );
                                            }}
                                        >
                                            {t("app.orders.import_example_file", {defaultValue: "Scarica file di esempio"})}
                                        </div>
                                    </p>
                                )}
                            </>
                        )}

                        <div className="col-span-2 mt-3">
                            <label className="block text-sm font-medium mb-1 text-gray-900">
                                {t("app.orders.import_file_title", {defaultValue: "File da importare"})}
                            </label>
                            <CSVReader
                                cssClass={selectedParamsConfig ? "p-2 border-2 mt-1 border-gray-300 border-dashed rounded-md" : "p-2 border-2 mt-1 border-gray-300 border-dashed rounded-md opacity-50"}
                                cssInputClass={"text-sm font-medium text-indigo-600"}
                                cssLabelClass={""}
                                label=""
                                disabled={!selectedParamsConfig}
                                onFileLoaded={handleForce}
                                parserOptions={options}
                            />
                        </div>

                        {(columns.length > 0 && selectedParamsConfig) && (
                            <>
                                {importModels.length > 0 && (
                                    <div className="col-span-2 mt-3">
                                        <Controller
                                            name="import_model"
                                            rules={{required: false}}
                                            control={control}
                                            render={({
                                                field: {onChange, value, name},
                                                fieldState: {error},
                                            }) => (
                                                <InputSelect
                                                    label={t("app.import_model.select_model", {defaultValue: "Seleziona modello"})}
                                                    name={name}
                                                    options={importModels}
                                                    value={value}
                                                    onChange={(nextValue) => {
                                                        onChange(nextValue);
                                                        if (nextValue) {
                                                            setFoundModel(true);
                                                            setConfirmedHeader(nextValue.params);
                                                        }
                                                    }}
                                                    isLoading={isLoadingModels}
                                                    isDisabled={isLoadingModels}
                                                    errors={error}
                                                />
                                            )}
                                        />
                                    </div>
                                )}

                                <div className="col-span-2 mt-3">
                                    <label className="block text-sm font-medium text-gray-900">
                                        {t("app.orders.import_configuration_title", {defaultValue: "Configura colonne"})}
                                    </label>
                                    <CsvTable
                                        params={selectedParamsConfig.data}
                                        columns={columns}
                                        rows={rows}
                                        onSetConfirmedHeader={(value, data) => {
                                            const items = confirmedHeader;
                                            lodash.remove(confirmedHeader, (entry) => entry.file_param === data);
                                            setConfirmedHeader([...items, {file_param: data, confirmed_param: value}]);
                                        }}
                                        confirmedHeader={confirmedHeader}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {exception && <Warning message={exception} />}
                </div>

                {isOpenModel && (
                    <ImportModelPopup
                        isOpen={isOpenModel}
                        setIsOpen={setOpenModel}
                        importType={selectedImport?.type}
                        columnsCode={columnsCode}
                        confirmedHeader={confirmedHeader}
                        onConfirmed={() => {
                            uploadCsv(originalFile);
                        }}
                    />
                )}
            </SlideTopPanel>
        </>
    );
}

const Footer = ({extras}) => {
    const {t} = useTranslation();

    return (
        <div className="flex-shrink-0 before:flex-1 px-4 py-4 flex justify-between">
            <ButtonSubmit
                isLoading={extras.isLoading}
                label={t("app.common.confirm_import", {defaultValue: "Conferma import"})}
                isFullWith={false}
                onClick={() => {
                    extras.onStartLoad();
                }}
                disabled={extras.confirmedHeader.length === 0}
            />
        </div>
    );
};
